import {
  useConfig,
  useValidation,
  type BaseConfig,
  type Rules,
} from '@seek/libs-shared';
import { useTranslations } from '@vocab/react';
import { Stack } from 'braid-design-system';
import { ReviewPageLayout } from '../components/ReviewPageLayout/ReviewPageLayout';
import { Progress, type PageProps, type ReviewPayload } from '../type';
import { validationRules } from '../validation/validationRules';
import translations from './.vocab';

import { useEffect } from 'react';
import { useFeatureFlags } from '../../shared/providers/featureFlagProvider';
import type { SearchCompanyProfileResult } from '../../shared/services/companyProfileService/types';
import {
  trackClickOverallPageContinue,
  trackClickOverallPageSubSection,
  trackDisplayOverallPage,
  trackValidationErrorOverallPage,
  type ReviewFormContext,
} from '../../shared/tracking/writeAReview/trackingEvents';
import type { Rating } from '../components/StarSelector/StarSelector';
import {
  CompanyNameQuestion,
  type CompanyNameValue,
} from '../questions/AutoSuggestionQuestions/CompanyNameQuestion/CompanyNameQuestion';
import { CompanyNameQuestionV2 } from '../questions/AutoSuggestionQuestions/CompanyNameQuestion/CompanyNameQuestionV2';
import { OverallRatingQuestion } from '../questions/RatingQuesions/OverallRatingQuestion';
import { mapErrorMessage } from '../utils/mapErrorMessage';

export interface OverallRatingPageProps extends PageProps {
  searchCompanyProfiles: (query: string) => Promise<SearchCompanyProfileResult>;
  hideCompanyNameSearch?: boolean;
}

const CompanyNameField = ({
  handleCompanyNameChange,
  onSearchCompanyProfiles,
  review,
  hideCompanyNameSearch,
  config,
  reviewFormContext,
  tone,
  message,
}: {
  config: BaseConfig;
  handleCompanyNameChange: (value: CompanyNameValue) => void;
  onSearchCompanyProfiles: (
    query: string,
  ) => Promise<SearchCompanyProfileResult>;
  review: ReviewPayload;
  reviewFormContext: ReviewFormContext;
  tone: 'critical' | 'neutral';
  hideCompanyNameSearch?: boolean;
  message?: string;
}) => {
  const { cookieFeatureFlags } = useFeatureFlags();

  if (hideCompanyNameSearch) {
    return null;
  }

  if (cookieFeatureFlags.COMPANY_NAME_FIELD_V2) {
    return (
      <CompanyNameQuestionV2
        onChange={handleCompanyNameChange}
        searchCompanyProfiles={onSearchCompanyProfiles}
        text={review?.companyName}
        value={{
          companyId: review?.companyId,
          organisationId: review?.organisationId,
          companyName: review?.companyName,
        }}
        tone={tone}
        message={message}
        onValueSelected={() =>
          trackClickOverallPageSubSection({
            config,
            context: reviewFormContext,
            currentPageSubsection: 'company_name',
          })
        }
      />
    );
  }

  return (
    <CompanyNameQuestion
      onChange={handleCompanyNameChange}
      searchCompanyProfiles={onSearchCompanyProfiles}
      text={review?.companyName}
      value={{
        companyId: review?.companyId,
        organisationId: review?.organisationId,
        companyName: review?.companyName,
      }}
      tone={tone}
      message={message}
      onValueSelected={() =>
        trackClickOverallPageSubSection({
          config,
          context: reviewFormContext,
          currentPageSubsection: 'company_name',
        })
      }
    />
  );
};

export const OverallRatingPage = ({
  review,
  setPage,
  setter,
  searchCompanyProfiles,
  pageNumber,
  hideCompanyNameSearch,
  reviewFormContext,
}: OverallRatingPageProps) => {
  const { t } = useTranslations(translations);
  const config = useConfig();

  const fields = {
    companyName: review?.companyName,
    overallRating: review?.overallRating,
  };

  const { companyName, overallRating } = validationRules(config.language);

  const rules = {
    companyName,
    overallRating,
  } as Rules<typeof fields>;

  const {
    validateWithErrors,
    getTone,
    getMessage,
    handleValueChangeWithValidation,
  } = useValidation<ReviewPayload>(fields, rules);

  const handleOnContinue = () => {
    trackClickOverallPageContinue({ config, context: reviewFormContext });
    const { isValid, errors } = validateWithErrors();
    if (!isValid) {
      trackValidationErrorOverallPage({
        config,
        context: reviewFormContext,
        errorMessage: errors.map((error) => mapErrorMessage(error.fieldName)),
        errorText: errors.map((error) => error.message),
      });
      return;
    }
    setPage(Progress.YourRole);
  };

  const onSearchCompanyProfiles = async (query: string) => {
    return searchCompanyProfiles(query);
  };

  const handleCompanyNameChange = (value: CompanyNameValue) => {
    handleValueChangeWithValidation({
      setter,
      validatorProperty: 'companyName',
      previousValue: review,
    })(value.companyName);

    setter((prev) => ({
      ...prev,
      companyId: value.companyId,
      organisationId: value.organisationId,
      companyProfilePublished: value.published,
    }));
  };

  const handleOverallRatingChange = (value: Rating) => {
    handleValueChangeWithValidation({
      setter,
      validatorProperty: 'overallRating',
      previousValue: review,
    })(value);

    trackClickOverallPageSubSection({
      config,
      context: reviewFormContext,
      currentPageSubsection: 'overall_experience',
    });
  };

  useEffect(() => {
    trackDisplayOverallPage({ config, context: reviewFormContext });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ReviewPageLayout
      progress={pageNumber}
      companyName={review?.companyName}
      subtitle={t(
        'Your review will help someone else and it only takes a few minutes. All reviews are anonymous. We wont share your name, only details about your role.',
      )}
      logo={review?.branding?.logoImageUrl}
      onContinue={handleOnContinue}
      testid="overall-rating-page"
    >
      <Stack space="xlarge">
        <CompanyNameField
          handleCompanyNameChange={handleCompanyNameChange}
          onSearchCompanyProfiles={onSearchCompanyProfiles}
          review={review}
          hideCompanyNameSearch={hideCompanyNameSearch}
          config={config}
          reviewFormContext={reviewFormContext}
          tone={getTone('companyName')}
          message={getMessage('companyName')}
        />
        <OverallRatingQuestion
          onChange={handleOverallRatingChange}
          value={review?.overallRating}
          message={getMessage('overallRating')}
        />
      </Stack>
    </ReviewPageLayout>
  );
};
